import React, { useState, useEffect, useRef } from "react";
import { ref, set, push, onValue, update, remove } from "firebase/database";
import { db } from "./firebase";
import { FiPlus, FiTrash2, FiEdit2, FiSave, FiX, FiChevronDown, FiChevronUp } from "react-icons/fi";
import { formatCourseId } from "./utils/formatCourse";

const AdminPDFs = () => {
    const [courses, setCourses] = useState([]);
    const [activeCourse, setActiveCourse] = useState("");
    const [pdfs, setPdfs] = useState({});
    const [chapterList, setChapterList] = useState([]);
    const [chapter, setChapter] = useState("");
    const [pdfName, setPdfName] = useState("");
    const [pdfLink, setPdfLink] = useState("");
    const [isPublished, setIsPublished] = useState(true);
    const [isAddFormExpanded, setIsAddFormExpanded] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [newChapter, setNewChapter] = useState("");
    const [addingChapter, setAddingChapter] = useState(false);
    const [chapterAddError, setChapterAddError] = useState("");
    const initialCourseSet = useRef(false);
    const [editingPDF, setEditingPDF] = useState({ chapter: null, pdfKey: null });
    const [editPdfName, setEditPdfName] = useState("");
    const [editPdfLink, setEditPdfLink] = useState("");
    const [editIsPublished, setEditIsPublished] = useState(true);

    // Fetch courses
    useEffect(() => {
        const coursesRef = ref(db, "courses");
        return onValue(coursesRef, (snapshot) => {
            if (snapshot.exists()) {
                const keys = Object.keys(snapshot.val());
                setCourses(keys);
                // Only set activeCourse on very first load
                if (!initialCourseSet.current && keys.length > 0) {
                    setActiveCourse(keys[0]);
                    initialCourseSet.current = true;
                }
            }
        });
    }, []);

    // Ensure all PDFs have a published attribute (migration on load)
    useEffect(() => {
        if (!activeCourse) return;
        const pdfsRef = ref(db, `courses/${activeCourse}/pdfs`);
        return onValue(pdfsRef, (snapshot) => {
            if (snapshot.exists()) {
                const pdfData = snapshot.val();
                setPdfs(pdfData);
                setChapterList(Object.keys(pdfData));
                // Add published: true to any PDF missing it
                Object.entries(pdfData).forEach(([chapter, pdfs]) => {
                    if (typeof pdfs === 'object' && pdfs !== null) {
                        Object.entries(pdfs).forEach(([pdfKey, pdf]) => {
                            if (pdfKey !== 'name' && pdf && typeof pdf === 'object' && !('published' in pdf)) {
                                const pdfRef = ref(db, `courses/${activeCourse}/pdfs/${chapter}/${pdfKey}`);
                                update(pdfRef, { published: true });
                            }
                        });
                    }
                });
            } else {
                setPdfs({});
                setChapterList([]);
            }
        });
    }, [activeCourse]);

    // Add new folder (node) under pdfs
    const handleAddChapter = async () => {
        setChapterAddError("");
        const trimmed = newChapter.trim();
        if (!trimmed) {
            setChapterAddError("Folder name cannot be empty");
            return;
        }
        if (chapterList.includes(trimmed)) {
            setChapterAddError("Folder already exists");
            return;
        }
        setAddingChapter(true);
        try {
            await set(ref(db, `courses/${activeCourse}/pdfs/${trimmed}`), { name: trimmed });
            setNewChapter("");
        } catch (err) {
            setChapterAddError("Error adding folder: " + err.message);
        }
        setAddingChapter(false);
    };

    // Add PDF under selected folder
    const handleAddPDF = async () => {
        if (!chapter || !pdfName || !pdfLink) {
            alert("Please fill in all fields");
            return;
        }
        const chapterRef = ref(db, `courses/${activeCourse}/pdfs/${chapter}`);
        await push(chapterRef, {
            pdfName,
            pdfLink,
            published: isPublished
        });
        setPdfName("");
        setPdfLink("");
        setIsPublished(true);
        setIsAddFormExpanded(false);
    };

    // Edit PDF
    const handleEditPDF = (chapter, pdfKey, pdf) => {
        setEditingPDF({ chapter, pdfKey });
        setEditPdfName(pdf.pdfName);
        setEditPdfLink(pdf.pdfLink);
        setEditIsPublished(pdf.published !== false);
    };

    // Save PDF edit
    const handleSavePDF = async () => {
        const { chapter, pdfKey } = editingPDF;
        if (!editPdfName || !editPdfLink) {
            alert("Please fill in all fields");
            return;
        }
        const pdfRef = ref(db, `courses/${activeCourse}/pdfs/${chapter}/${pdfKey}`);
        await update(pdfRef, {
            pdfName: editPdfName,
            pdfLink: editPdfLink,
            published: editIsPublished
        });
        setEditingPDF({ chapter: null, pdfKey: null });
    };

    // Cancel edit
    const handleCancelEdit = () => {
        setEditingPDF({ chapter: null, pdfKey: null });
    };

    // Delete PDF
    const handleDeletePDF = async (chapter, pdfKey) => {
        if (window.confirm("Are you sure you want to delete this PDF?")) {
            const pdfRef = ref(db, `courses/${activeCourse}/pdfs/${chapter}/${pdfKey}`);
            await remove(pdfRef);
        }
    };

    // Toggle published status directly from the list
    const handleTogglePublished = async (chapter, pdfKey, currentStatus) => {
        const pdfRef = ref(db, `courses/${activeCourse}/pdfs/${chapter}/${pdfKey}`);
        await update(pdfRef, { published: !currentStatus });
    };

    // Filter PDFs by search
    const filteredPdfs = React.useMemo(() => {
        if (!searchQuery) return pdfs;
        const filtered = {};
        Object.entries(pdfs).forEach(([chapterName, chapterPdfs]) => {
            const matchingPdfs = Object.entries(chapterPdfs || {}).filter(([_, p]) =>
                (p.pdfName || "").toLowerCase().includes(searchQuery.toLowerCase())
            );
            if (matchingPdfs.length > 0) {
                filtered[chapterName] = Object.fromEntries(matchingPdfs);
            }
        });
        return filtered;
    }, [pdfs, searchQuery]);

    return (
        <div className="p-4 text-black min-h-screen">
            <h1 className="text-3xl font-bold mb-6 text-black">PDFs</h1>

            {/* Course Tabs */}
            <div className="flex space-x-2 mb-6 p-2">
                {courses.map(course => (
                    <button
                        key={course}
                        onClick={() => setActiveCourse(course)}
                        className={`px-4 py-2 rounded-md transition-colors ${activeCourse === course
                            ? "bg-teal-600 text-white"
                            : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                            }`}
                    >
                        {formatCourseId(course)}
                    </button>
                ))}
            </div>

            {/* Add Folder Section */}
            <div className="mb-6 bg-white p-4 rounded-lg shadow-md">
                <h2 className="text-lg font-semibold mb-2">Add Folder</h2>
                <div className="flex flex-col sm:flex-row gap-2 items-center">
                    <input
                        type="text"
                        placeholder="Folder name"
                        value={newChapter}
                        onChange={e => setNewChapter(e.target.value)}
                        className="p-2 border rounded-md flex-1"
                    />
                    <button
                        onClick={handleAddChapter}
                        className="bg-teal-600 text-white px-4 py-2 rounded-md hover:bg-teal-700"
                        disabled={addingChapter}
                    >
                        {addingChapter ? "Adding..." : "Add Folder"}
                    </button>
                </div>
                {chapterAddError && <div className="text-red-600 mt-2">{chapterAddError}</div>}
                {chapterList.length > 0 && (
                    <div className="mt-2 text-sm text-gray-600">
                        <span>Folders: </span>
                        {chapterList.map((ch, idx) => (
                            <span key={idx} className="inline-block bg-gray-200 text-gray-800 px-2 py-1 rounded mr-1 mb-1">{ch}</span>
                        ))}
                    </div>
                )}
            </div>

            {/* Search and Add Button */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <input
                    type="text"
                    placeholder="Search PDFs..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    className="p-2 border rounded-md"
                />
                <button
                    onClick={() => setIsAddFormExpanded(!isAddFormExpanded)}
                    className="bg-green-600 text-white p-2 rounded-md hover:bg-green-700"
                >
                    {isAddFormExpanded ? "Cancel" : "Add New PDF"}
                </button>
            </div>

            {/* Add PDF Form */}
            {isAddFormExpanded && (
                <div className="bg-white p-4 rounded-lg shadow-md mb-6">
                    <h2 className="text-xl font-semibold mb-4">Add New PDF</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium mb-1">Folder*</label>
                            <select
                                value={chapter}
                                onChange={e => setChapter(e.target.value)}
                                className="w-full p-2 border rounded-md"
                                required
                            >
                                <option value="">Select folder</option>
                                {chapterList.map((ch) => (
                                    <option key={ch} value={ch}>{ch}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-1">PDF Name*</label>
                            <input
                                type="text"
                                value={pdfName}
                                onChange={e => setPdfName(e.target.value)}
                                className="w-full p-2 border rounded-md"
                                required
                            />
                        </div>
                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium mb-1">PDF Link*</label>
                            <input
                                type="text"
                                value={pdfLink}
                                onChange={e => setPdfLink(e.target.value)}
                                className="w-full p-2 border rounded-md"
                                required
                            />
                        </div>
                        <div className="md:col-span-2">
                            <label className="flex items-center space-x-2">
                                <input
                                    type="checkbox"
                                    checked={isPublished}
                                    onChange={e => setIsPublished(e.target.checked)}
                                    className="h-4 w-4 text-teal-600 rounded"
                                />
                                <span className="text-sm font-medium">Publish to students</span>
                            </label>
                        </div>
                    </div>
                    <div className="mt-4 flex justify-end space-x-2">
                        <button
                            onClick={() => setIsAddFormExpanded(false)}
                            className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleAddPDF}
                            className="bg-teal-600 text-white px-4 py-2 rounded-md hover:bg-teal-700"
                        >
                            Add PDF
                        </button>
                    </div>
                </div>
            )}

            {/* PDFs Display */}
            {Object.keys(filteredPdfs).length > 0 ? (
                <div className="divide-y divide-gray-200">
                    {Object.entries(filteredPdfs).map(([chapterName, chapterPdfs]) => (
                        <div key={chapterName} className="p-4">
                            <h3 className="text-lg font-semibold mb-3">{chapterName}</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {Object.entries(chapterPdfs || {})
                                    .filter(([pdfKey, p]) => pdfKey !== 'name')
                                    .map(([pdfKey, p]) => (
                                        <div key={pdfKey} className="border rounded-md p-3 flex flex-col hover:bg-gray-50">
                                            {editingPDF.chapter === chapterName && editingPDF.pdfKey === pdfKey ? (
                                                <>
                                                    <input
                                                        type="text"
                                                        value={editPdfName}
                                                        onChange={e => setEditPdfName(e.target.value)}
                                                        className="w-full mb-2 p-2 border rounded-md"
                                                        placeholder="PDF Name"
                                                    />
                                                    <input
                                                        type="text"
                                                        value={editPdfLink}
                                                        onChange={e => setEditPdfLink(e.target.value)}
                                                        className="w-full mb-2 p-2 border rounded-md"
                                                        placeholder="PDF Link"
                                                    />
                                                    <label className="flex items-center space-x-2 mb-2">
                                                        <input
                                                            type="checkbox"
                                                            checked={editIsPublished}
                                                            onChange={e => setEditIsPublished(e.target.checked)}
                                                            className="h-4 w-4 text-teal-600 rounded"
                                                        />
                                                        <span className="text-xs text-gray-600">Published</span>
                                                    </label>
                                                    <div className="flex space-x-2 mt-2">
                                                        <button
                                                            onClick={handleSavePDF}
                                                            className="bg-teal-600 text-white px-3 py-1 rounded-md hover:bg-teal-700"
                                                        >
                                                            Save
                                                        </button>
                                                        <button
                                                            onClick={handleCancelEdit}
                                                            className="bg-gray-500 text-white px-3 py-1 rounded-md hover:bg-gray-600"
                                                        >
                                                            Cancel
                                                        </button>
                                                    </div>
                                                </>
                                            ) : (
                                                <>
                                                    <div className="flex-1">
                                                        <p className="text-gray-900 font-medium mb-1">{p.pdfName}</p>
                                                        <a
                                                            href={p.pdfLink}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="text-teal-600 hover:text-teal-800 text-sm"
                                                        >
                                                            View PDF
                                                        </a>
                                                    </div>
                                                    <div className="flex items-center justify-between mt-2">
                                                        <label className="flex items-center space-x-1">
                                                            <input
                                                                type="checkbox"
                                                                checked={p.published !== false}
                                                                onChange={() => handleTogglePublished(chapterName, pdfKey, p.published !== false)}
                                                                className="h-4 w-4 text-teal-600 rounded cursor-pointer"
                                                            />
                                                            <span className="text-xs text-gray-600">Published</span>
                                                        </label>
                                                        <div className="flex space-x-1">
                                                            <button
                                                                onClick={() => handleEditPDF(chapterName, pdfKey, p)}
                                                                className="text-teal-600 hover:text-teal-800 p-1"
                                                                title="Edit"
                                                            >
                                                                <FiEdit2 className="h-5 w-5" />
                                                            </button>
                                                            <button
                                                                onClick={() => handleDeletePDF(chapterName, pdfKey)}
                                                                className="text-red-600 hover:text-red-800 p-1"
                                                                title="Delete"
                                                            >
                                                                <FiTrash2 className="h-5 w-5" />
                                                            </button>
                                                        </div>
                                                    </div>
                                                    {p.published === false && (
                                                        <span className="text-xs text-red-600 mt-1">(Hidden from students)</span>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    ))}
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="p-4 text-center text-gray-500">
                    No PDFs found matching your criteria
                </div>
            )}
        </div>
    );
};

export default AdminPDFs; 